import { useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';

type StringMapInputProps = {
	value?: Record<string, string> | null;
	onValueChange: (value: Record<string, string> | undefined) => void;
	title?: string;
	optional?: boolean;
	readOnly?: boolean;
};

type AttributeRow = { id: number; name: string; value: string };

/** Edits string dictionaries without changing their JSON object representation. */
export default function StringMapInput({ value, onValueChange, title = 'Attributes', optional = true, readOnly = false }: StringMapInputProps) {
	const [open, setOpen] = useState(false);
	const [rows, setRows] = useState<AttributeRow[]>([]);
	const nextId = useRef(0);
	const entries = Object.entries(value ?? {});
	const errors = rows.map(row => !row.name.trim() ? 'Enter an attribute name.'
		: rows.some(other => other.id !== row.id && other.name === row.name) ? 'Attribute names must be unique.' : undefined);

	function openEditor() {
		setRows(entries.map(([name, item]) => ({ id: nextId.current++, name, value: item })));
		setOpen(true);
	}

	function updateRow(id: number, field: 'name' | 'value', updated: string) {
		setRows(current => current.map(row => row.id === id ? { ...row, [field]: updated } : row));
	}

	return <Box sx={{ width: '100%', minWidth: 0, mt: 0.5 }}>
		<Button size="small" startIcon={<EditOutlinedIcon fontSize="small" />} onClick={openEditor}
			aria-label={`${readOnly ? 'View' : 'Edit'} ${title}`} sx={{ textTransform: 'none' }}>
			{entries.length ? `${entries.length} attribute${entries.length === 1 ? '' : 's'}` : readOnly ? 'No attributes' : 'Configure attributes'}
		</Button>
		{entries.length > 0 && <Typography variant="caption" color="text.secondary" component="div"
			sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entries.map(([name]) => name).join(', ')}</Typography>}
		<Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm" aria-labelledby="string-map-editor-title">
			<DialogTitle id="string-map-editor-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<Box component="span" sx={{ flex: 1, minWidth: 0 }}>{title}</Box>
				<IconButton size="small" aria-label="Close attributes editor" onClick={() => setOpen(false)}><CloseIcon /></IconButton>
			</DialogTitle>
			<DialogContent dividers>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Attribute names and values are stored as text. Names are case-sensitive.</Typography>
				<Stack spacing={2}>
					{rows.length === 0 && <Typography variant="body2" color="text.secondary">No attributes configured.</Typography>}
					{rows.map((row, index) => <Box key={row.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.5, bgcolor: 'background.default' }}>
						<Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) minmax(0, 1.5fr)' }, gap: 1.5, flex: 1, minWidth: 0 }}>
							<TextField label="Name" size="small" value={row.name} disabled={readOnly} error={!!errors[index]} helperText={errors[index]}
								onChange={event => updateRow(row.id, 'name', event.target.value)} slotProps={{ htmlInput: { 'aria-label': `Attribute ${index + 1} name` } }} />
							<TextField label="Value" size="small" value={row.value} disabled={readOnly} multiline maxRows={6}
								onChange={event => updateRow(row.id, 'value', event.target.value)} slotProps={{ htmlInput: { 'aria-label': `Attribute ${index + 1} value` } }} />
						</Box>
						{!readOnly && <Tooltip title="Remove attribute"><IconButton size="small" aria-label={`Remove attribute ${index + 1}`} onClick={() => setRows(current => current.filter(item => item.id !== row.id))} sx={{ mt: 0.5 }}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>}
					</Box>)}
					{!readOnly && <Button size="small" startIcon={<AddIcon />} onClick={() => setRows(current => [...current, { id: nextId.current++, name: '', value: '' }])} sx={{ alignSelf: 'flex-start' }}>Add attribute</Button>}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => setOpen(false)}>{readOnly ? 'Close' : 'Cancel'}</Button>
				{!readOnly && <Button variant="contained" disabled={errors.some(Boolean)} onClick={() => {
					onValueChange(rows.length === 0 && optional ? undefined : Object.fromEntries(rows.map(row => [row.name, row.value])));
					setOpen(false);
				}}>Apply</Button>}
			</DialogActions>
		</Dialog>
	</Box>;
}
